import type { SearchType } from '@/objects/generated/enums/SearchType';
import { arrayUnique } from './arrayUtils';

export type SearchStringFragments = {
    numbers: Set<number>;
    strings: string[];
};

/**
 * Converts a querystring to regexp patterns that encapsulates the search type
 * Note: The /g flag makes the Regexp stateful, it will remember the last match. So you can not do more than one match on the regexp.
 * @param query The querystring that we want to create a search pattern from
 * @param searchType The search type that we want to use
 * @returns An array of regexp patterns that encapsulates the search type
 */
export const CreateRegexpSearchPatternForSearchType = (query: string, searchType: SearchType) => {
    // We need to escape illegal regexp characters. space is not an illegal character, so we do not escape it.
    query = query.trim().replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');

    switch (searchType) {
        case 'StartsWith':
            return new RegExp(`^${query}`, 'i'); // ^ means start of string
        case 'EndsWith':
            return new RegExp(`${query}$`, 'i'); // $ means end of string
        case 'ExactWord':
            // If we are searching for "Eina" we will match "Eina rittet", but not "Einarittet"
            return new RegExp(`\\b${query}\\b`, 'i'); // \b means word boundary.
        case 'Contains':
            return new RegExp('(' + query.split('\\ ').join('|') + ')', 'gi'); // | means or
        case 'ContainsAll':
            return new RegExp('(?=.*' + query.split('\\ ').join(')(?=.*') + ')', 'gi'); // Positive lookahead
        default:
            throw new Error(`Unknown search type: ${searchType}`);
    }
};

/**
 * Turns a string into search fragments
 *
 * We create one array of numeric patterns and one of strings.
 *
 * Given the pattern "123 125-130 Test Demo" we will create:
 * ```js
 * {
 * numerics: [123, 125, 126, 127, 128, 129, 130],
 * strings: ['Test', 'Demo']
 * }
 * ```
 *
 * Note: This splits words by space AND COMMA, so a words are separated by commas. Example:
 * ```js
 * "Test,Demo,Yeah" => { numerics: [], strings: ['Test', 'Demo', 'Yeah'] }
 * ```
 *
 * @param pattern The search string we want to decompose
 */
const searchFragmentsCache = new Map<string, SearchStringFragments>();

export const CreateSearchFragmentsFromPattern = (pattern: string): SearchStringFragments => {
    if (!pattern.length) {
        return { numbers: new Set(), strings: [] };
    }

    const cached = searchFragmentsCache.get(pattern);
    if (cached !== undefined) {
        return cached;
    }
    // We replace commas with spaces and split the string into an array of strings
    const commaAndSpaceSeparatedFragments = pattern.replace(/,/g, ' ').split(' ');

    const numbers: Set<number> = new Set();
    const strings: string[] = [];
    // we loop throught the array and convert each string to a number
    commaAndSpaceSeparatedFragments.forEach((element) => {
        if (element === '') {
            return;
        }
        // If the string contains a hyphen, we split the string into an array of strings
        const rangeValue = element.split('-');
        if (rangeValue.length === 1) {
            const startRange = parseInt(rangeValue[0]);

            // If its not a number, add it as a string
            if (isNaN(startRange)) {
                strings.push(rangeValue[0]);
                return;
            }

            numbers.add(startRange);
        } else {
            // We check if the range is only two elements. That means that it either starts with a - or ends with a -
            let startRange = rangeValue[0];
            let endRange = rangeValue[1];

            if (startRange == '') {
                startRange = '0';
            } else if (endRange == '') {
                endRange = '9999';
            }

            if (isNaN(parseInt(startRange)) || isNaN(parseInt(endRange))) {
                // We have something that is not a number, but contains the a dash. Add the original element as a string
                // So "Jean-Claude" will be added instead of "Jean","Claude"
                strings.push(element);
                return;
            }

            // We create a range of numbers from the first to the last number
            // NB! You might be tempted to do range(start, end) here, but that will be slower
            const start = parseInt(startRange);
            const end = parseInt(endRange);
            for (let num = start; num <= end; num++) {
                numbers.add(num);
            }
        }
    });

    const result: SearchStringFragments = {
        numbers,
        strings: arrayUnique(strings),
    };

    if (searchFragmentsCache.size >= 10) {
        searchFragmentsCache.delete(searchFragmentsCache.keys().next().value!);
    }
    searchFragmentsCache.set(pattern, result);

    return result;
};

/**
 * Create a list of numbers from a pattern
 * @param pattern
 * @returns
 */
export const CreateNumbersFromPattern = (pattern: string) => {
    return CreateSearchFragmentsFromPattern(pattern).numbers;
};

/**
 * This will return true if parts of every word of the pattern is found in the searchstring
 * @param searchValue // The value we want to find the pattern in
 * @param pattern // The pattern we want to find in the searchvalue. It will be split on spaces into words and all words must be found in the searchvalue
 */
export const SearchStringFromPattern = (searchValue: string, pattern: string) => {
    const words = pattern?.toLocaleLowerCase()?.split(' ');
    searchValue = searchValue.toLocaleLowerCase();

    let found = true;
    words.forEach((word) => {
        if (word?.trim() != '' && !searchValue.includes(word)) {
            found = false;
            return;
        }
    });
    return found;
};
